<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="anyLoading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <form-card
            :title="$t('pages.videoList.detailModal.general.title')"
            :description="$t('pages.videoList.detailModal.general.description')"
          >
            <div class="flex flex-col gap-y-5">
              <multi-select
                v-model="rawVideo.language"
                :error="errors?.language"
                :searchable="false"
                :label="$t('video.language')"
                :options="fetchLanguages"
              />
              <TextInput
                v-model="rawVideo.title"
                type="text"
                name="title"
                :error="errors?.title"
                :label="$t('video.title')"
              />
              <TextInput
                v-model="rawVideo.description"
                type="text"
                name="description"
                :error="errors?.description"
                :label="$t('video.description')"
              />
            </div>
          </form-card>
          <form-card
            :title="$t('pages.videoList.detailModal.order.title')"
            :description="$t('pages.videoList.detailModal.order.description')"
          >
            <TextInput
              v-model="rawVideo.orderNumber"
              type="number"
              name="orderNumber"
              :error="errors?.orderNumber"
              :label="$t('video.orderNumber')"
            />
          </form-card>
          <form-card
            :title="$t('pages.videoList.detailModal.categories.title')"
            :description="$t('pages.videoList.detailModal.categories.description')"
          >
            <multiselect-tags
              v-model="rawVideo.categories"
              :label="$t('video.categories')"
              :options="videoCategories"
              :object="false"
            />
          </form-card>
          <form-card
            :title="$t('pages.videoList.detailModal.video.title')"
            :description="$t('pages.videoList.detailModal.video.description')"
          >
            <medium
              v-model="file"
              :aspect="0.5"
              with-button
              :thumbnail="false"
              @click="openFiles"
            />
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {number, object, string} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import TextInput from '@utils/inputs/Input.vue';
import MultiSelect from '@utils/Multiselect.vue';
import Medium from '@utils/inputs/Medium.vue';
import MultiselectTags from '@utils/MultiselectTags.vue';
import { Upload, useSingleFileUpload } from '@utils/upload/fileUpload';
import { collOptionsProvider, enumOptionsProvider } from '@utils/table/hooks/provider';
import { syncRef } from '@hooks/state';
import type { Video, VideoCategory, WbFile } from '@/entities/Entities';

interface Props {
  open: boolean;
  video?: Video;
}

const emit = defineEmits(['update:open', 'update:video', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalVideo = useVModel(props, 'video', emit);

const isNew = computed(() => !props.video);
const tKey = computed(() => (isNew.value ? 'pages.videoList.detailModal.new' : 'pages.videoList.detailModal.edit'));

const prevFile = computed(() => (props.video && props.video.file ? Upload.from(props.video.file) : undefined));

const {
  file,
  open: openFiles,
  uploadDiff,
  reset: resetFiles,
  loading: uploading,
  getUpdatedFiles,
} = useSingleFileUpload<WbFile>(prevFile);

const { t } = useI18n();

const notify = useNotify();

const initialData = (
  video?: Video,
): Partial<Omit<Video, 'categories'>> & { categories: string[] } => (video
  ? {
    ...video,
    categories: video.categories.map((category) => category['@id']),
  }
  : {
    title: undefined,
    description: undefined,
    language: undefined,
    categories: [],
    orderNumber: undefined,
  });

const rawVideo = syncRef(internalVideo, (video) => ({ ...initialData(video) }));

const fetchLanguages = enumOptionsProvider('/enums/language', (v) => ({
  value: v,
  label: t(`language.${v}`),
}));

const {
  data: video,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    title: string().nullable().required(t('validation.notEmpty')),
    language: string().nullable().required(t('validation.notEmpty')),
    orderNumber: number().nullable().required(t('validation.notEmpty')),
  }),
  rawVideo,
);

function resetData() {
  rawVideo.value = { ...initialData() } as unknown as typeof video['value'];
  setValidation(false);
  resetFiles();
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalVideo.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const anyLoading = computed(() => loading.value || uploading.value);

const save = validatedFn(async () => {
  try {
    const uploadFiles = getUpdatedFiles(await uploadDiff());

    const edit = !!props.video;
    const url = edit ? props.video['@id'] : '/videos';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        title: video.value.title,
        description: video.value.description,
        categories: video.value.categories,
        file: uploadFiles.at(0)?.file['@id'] ?? null,
        language: video.value.language,
        orderNumber: video.value.orderNumber,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
    resetFiles();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});

const videoCategories = collOptionsProvider<VideoCategory>('/video_categories', {}, (value) => ({
  label:
    value.translations.find(({ language }) => language === 'de')?.title
    ?? value.translations[0]?.title
    ?? value.id,
  value: value['@id'],
}));
</script>

<style lang="scss" scoped></style>

